import React from 'react';
import PhoneInput, { CountryData } from 'react-phone-input-2';
import 'react-phone-input-2/lib/high-res.css';
import styled from 'styled-components';
import { Box, ErrorText, Label, getTheme } from '@flightstats/airframe-react';

const colors = getTheme().colors;

export type IntlPhoneInputValue = {
  number: string;
  countryCode: string;
  dialCode: string;
};

export type InternationalPhoneInputProps = {
  label: string;
  disabled: boolean;
  countryCode?: string;
  onlyCountries: string[];
  phoneNumber: string;
  error?: boolean;
  helperText?: string;
  onBlurHandler: (event: React.FocusEvent<HTMLInputElement>, value: IntlPhoneInputValue) => void;
  onChange: (value: IntlPhoneInputValue) => void;
  id: string;
};

const componentStyle = {
  borderColor: colors.border.form.element.base,
  borderWidth: '2px',
};

const disableStyle = {
  borderColor: colors.border.form.element.disabled,
  backgroundColor: colors.background.form.element.disabled,
};

const errorStyle = {
  borderColor: colors.feedback.error,
};

const inputStyle = {
  ...componentStyle,
  color: colors.text.form.element.base,
  fontFamily: 'Montserrat,Arial,sans-serif',
  height: '40px',
  width: '100%',
  marginTop: '35px',
};

const disabledInputStyle = {
  ...inputStyle,
  ...disableStyle,
  color: colors.text.form.element.disabled,
};

const errorInputStyle = {
  ...inputStyle,
  ...errorStyle,
};

const buttonStyle = {
  ...componentStyle,
};

const disabledButtonStyle = {
  ...buttonStyle,
  ...disableStyle,
  opacity: '50%',
};

const errorButtonStyle = {
  ...buttonStyle,
  ...errorStyle,
};

const StyledInputLabel = styled(Label)<{ disabled: boolean }>`
  font-weight: 700;
  ${({ disabled }) =>
    disabled &&
    `
    color:  ${colors.disabledLightOnDark};
  `}
  line-height: 1.5rem;
`;

const getInputStyle = (disabled: boolean, error: boolean) => {
  if (disabled) {
    return disabledInputStyle;
  } else if (error) {
    return errorInputStyle;
  } else {
    return inputStyle;
  }
};

const getButtonStyle = (disabled: boolean, error: boolean) => {
  if (disabled) {
    return disabledButtonStyle;
  } else if (error) {
    return errorButtonStyle;
  } else {
    return buttonStyle;
  }
};

export const InternationalPhoneInput: React.FC<InternationalPhoneInputProps> = ({
  id,
  label,
  disabled,
  countryCode = 'us',
  onlyCountries,
  phoneNumber,
  error = false,
  helperText,
  onBlurHandler,
  onChange,
}) => {
  return (
    <Box className='int-phone-input-box' id={id}>
      <StyledInputLabel disabled={disabled}>{label}</StyledInputLabel>
      <PhoneInput
        disableDropdown={onlyCountries.length === 1}
        countryCodeEditable={false}
        disabled={disabled}
        country={countryCode.toLowerCase()}
        onlyCountries={onlyCountries.map(onlyCountry => onlyCountry.toLowerCase())}
        value={phoneNumber}
        inputStyle={getInputStyle(disabled, error)}
        buttonStyle={getButtonStyle(disabled, error)}
        onBlur={(event) => onBlurHandler(event, { number: phoneNumber, countryCode, dialCode: '' })}
        onChange={(phone: string, country: CountryData) => {
          onChange({
            number: `+${phone}`,
            countryCode: country.countryCode,
            dialCode: country.dialCode,
          });
        }}
      />
      {error && <ErrorText idPrefix='' icon={error} helperText={helperText} />}
    </Box>
  );
};

export default InternationalPhoneInput;